"use client";

// Locale lives in a client-side store (cookie-backed), NOT the URL. This keeps
// the chosen language across any navigation - including 404s and arbitrary
// paths - because the DOM translator reads from here, not from a URL segment.

import { useSyncExternalStore } from "react";
import { defaultLocale, locales, type Locale } from "./config";

const COOKIE_NAME = "locale";
const ONE_YEAR = 60 * 60 * 24 * 365;

const listeners = new Set<() => void>();
// Cached snapshot so useSyncExternalStore stays stable between changes.
let current: Locale | null = null;

function isLocale(value: string): value is Locale {
  return (locales as readonly string[]).includes(value);
}

function cookieLocale(): Locale | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(/(?:^|; )locale=([^;]*)/);
  if (!match) return null;
  const value = decodeURIComponent(match[1]);
  return isLocale(value) ? value : null;
}

// Inbound links like /ja/pricing still seed the language on first visit; after
// that the cookie is authoritative and the URL prefix no longer matters.
function urlLocale(): Locale | null {
  if (typeof window === "undefined") return null;
  const seg = window.location.pathname.split("/")[1] ?? "";
  return isLocale(seg) ? seg : null;
}

function resolveInitial(): Locale {
  return cookieLocale() ?? urlLocale() ?? defaultLocale;
}

function persist(locale: Locale) {
  if (typeof document === "undefined") return;
  document.cookie = `${COOKIE_NAME}=${locale}; path=/; max-age=${ONE_YEAR}; samesite=lax`;
}

function emit() {
  for (const listener of listeners) listener();
}

function getSnapshot(): Locale {
  if (current === null) current = resolveInitial();
  return current;
}

// Server has no cookie access, so SSR always reports English -> no hydration
// mismatch (server HTML is always English and translated on the client).
function getServerSnapshot(): Locale {
  return defaultLocale;
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export function useLocaleStore(): Locale {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Persist the current locale to the cookie without notifying (first-visit seed). */
export function ensureStoredLocale() {
  persist(getSnapshot());
}

/** Set the active locale (explicit pick or adopted from the account session). */
export function setStoredLocale(locale: Locale) {
  const changed = current !== locale;
  current = locale;
  persist(locale);
  if (changed) emit();
}
